import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router }                from '@angular/router';
import { Subscription }                          from 'rxjs';
import { CsChatComponent, CsChatMessage }        from '@cs/components/chat';
import { AgentContextService }                   from '../services/agent-context.service';
import { PmChatService, PmChatSessionSummary, PmContextUsage } from '../services/pm-chat.service';
import { PmChatConversation }                    from '../services/pm-chat-conversation';

/**
 * Full-page "AI Data Analyst" — the deep-dive surface reached from the top-nav modulebar item (and
 * the bubble's Expand button). A saved-session sidebar plus the shared <cs-chat>, wired through the
 * same {@link PmChatConversation} state-machine as the bubble and carrying the same live page context
 * from {@link AgentContextService}. Uses cohelion-chat-angular's AppComponent as the wiring reference.
 */
@Component({
	standalone:      false,
	selector:        'pmc-ai-analyst',
	templateUrl:     './ai-analyst.component.html',
	styleUrls:       ['./ai-analyst.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiAnalystComponent implements OnInit, AfterViewInit, OnDestroy {

	sessions: PmChatSessionSummary[] = [];

	/**
	 * Whether to offer the advanced debug view (full system prompt + tool schemas + model config).
	 * Gated on the CF `Admin.Advanced` resource carried by the AI JWT; starts false and is only ever
	 * set true once that claim is confirmed, so the control is hidden for ordinary users. UI gating
	 * only — MCP enforces the same resource server-side.
	 */
	canDebug = false;

	@ViewChild(CsChatComponent) private chatView?: CsChatComponent;

	private readonly convo: PmChatConversation;

	/** Watches the surrounding layout so the page height is re-measured when nav/footer/viewport change. */
	private resizeObserver?: ResizeObserver;

	/** Subscription to the ?session= query param — the single source of truth for what's open. */
	private routeSub?: Subscription;

	constructor(
		private readonly chat: PmChatService,
		private readonly cdr: ChangeDetectorRef,
		private readonly agentContext: AgentContextService,
		private readonly host: ElementRef<HTMLElement>,
		private readonly zone: NgZone,
		private readonly router: Router,
		private readonly route: ActivatedRoute
	) {
		this.convo = new PmChatConversation(chat, cdr, {
			context:          () => this.agentContext.snapshot(),
			onSessionStarted: (startUtc, title, hasGeneratedTitle) => {
				this.upsertSession({ startUtc, title, hasGeneratedTitle });
				// The server assigns the key on the first turn — put it in the URL straight away so a
				// refresh mid-conversation reopens it instead of dropping the user on a blank chat.
				this.syncUrl(startUtc);
			},
			onTitle:          (startUtc, title) => {
				this.sessions = this.sessions.map(s => s.startUtc === startUtc ? { ...s, title } : s);
				this.cdr.markForCheck();
			}
		});
	}

	get messages(): CsChatMessage[]        { return this.convo.messages; }
	get busy(): boolean                    { return this.convo.busy; }
	get contextUsage(): PmContextUsage | undefined { return this.convo.contextUsage; }
	get debugContext() { return this.convo.debugContext; }
	onDebugRequested(): void { this.convo.loadDebugContext(); }
	get activeStartUtc(): string | null    { return this.convo.sessionStartUtc; }

	get chatTitle(): string {
		return this.sessions.find(s => s.startUtc === this.convo.sessionStartUtc)?.title ?? 'AI Data Analyst';
	}

	ngOnInit(): void {
		this.refreshSessions();

		// Resolve the admin entitlement once; stays false (control hidden) if the claim is absent or
		// the token can't be minted. OnPush, so nudge change detection when it flips.
		this.chat.canUseAdvancedDebug().then(allowed => {
			this.canDebug = allowed;
			this.cdr.markForCheck();
		});

		// The URL drives what's open, not the click. Selecting a session navigates; THIS reacts and
		// loads it. One code path then serves a click, a page refresh, browser back/forward, and a
		// pasted deep link — previously openSession() loaded the transcript without touching the URL,
		// so /ai-analyst always reopened blank however you got there.
		this.routeSub = this.route.queryParamMap.subscribe(params => {
			const startUtc = params.get('session');
			if (startUtc === this.convo.sessionStartUtc) { return; }   // already showing it (e.g. our own syncUrl)
			if (startUtc) {
				this.convo.loadSession(startUtc, () => setTimeout(() => this.chatView?.scrollToBottom()));
			} else {
				this.convo.reset();
			}
			this.cdr.markForCheck();
		});
	}

	ngAfterViewInit(): void {
		// Drive the page height from JS rather than CSS: the shell's router-outlet container only sets
		// a `min-height`, so a percentage height can't resolve reliably. Measure the host's real top
		// offset (the nav bars above it) and the footer height, and size the host to exactly fill the
		// gap between them. Runs outside Angular (pure style writes, no change detection) and re-measures
		// whenever the surrounding layout changes (viewport resize, the footer showing/hiding, etc.).
		this.zone.runOutsideAngular(() => {
			this.measureHeight();
			// A couple of deferred passes catch late layout (web-font swap, footer render).
			setTimeout(() => this.measureHeight());
			setTimeout(() => this.measureHeight(), 250);

			this.resizeObserver = new ResizeObserver(() => this.measureHeight());
			this.resizeObserver.observe(document.body);
		});
	}

	/** Fill the space between the nav bars above and the footer below. */
	private measureHeight(): void {
		const el     = this.host.nativeElement;
		const top    = el.getBoundingClientRect().top;                       // height of the nav bars above us
		const footer = document.querySelector<HTMLElement>('.shell--footer'); // outside our component; may be absent
		const footerH = footer ? footer.getBoundingClientRect().height : 0;
		const height  = Math.max(320, Math.floor(window.innerHeight - top - footerH));
		const next    = height + 'px';
		if (el.style.height !== next) { el.style.height = next; }            // guard avoids a ResizeObserver loop
	}

	onSend(text: string): void { this.convo.send(text); }
	onStop(): void             { this.convo.stop(); }

	/**
	 * Start a new chat. Resets directly AND drops ?session= — not just the navigate: an unsaved chat
	 * that never got a key (the stream failed before session.started) leaves the URL already bare, so
	 * syncUrl would no-op and the stale messages would stay on screen. Resetting first also makes the
	 * navigate's subscription callback a no-op, so nothing runs twice.
	 */
	newChat(): void {
		this.convo.reset();
		this.cdr.markForCheck();
		this.syncUrl(null);
	}

	/** Open a saved session by navigating; the route subscription does the loading. */
	openSession(session: PmChatSessionSummary): void { this.syncUrl(session.startUtc); }

	/**
	 * Reflects the open session in the URL as ?session=<startUtc> (or removes it for a new chat).
	 * `replaceUrl` so switching sessions doesn't stack history entries — Back should leave the page,
	 * not walk every conversation the user glanced at. A no-op when the URL already says this, which
	 * is what keeps syncUrl() and the queryParamMap subscription from ping-ponging.
	 */
	private syncUrl(startUtc: string | null): void {
		if (this.route.snapshot.queryParamMap.get('session') === startUtc) { return; }
		void this.router.navigate([], {
			relativeTo:         this.route,
			queryParams:        { session: startUtc },
			queryParamsHandling: 'merge',   // keep any unrelated params the shell put there
			replaceUrl:          true
		});
	}

	async onRenameTitle(title: string): Promise<void> {
		const startUtc = this.convo.sessionStartUtc;
		const next     = title.trim();
		if (!startUtc || !next) { return; }
		this.sessions = this.sessions.map(s => s.startUtc === startUtc ? { ...s, title: next, hasGeneratedTitle: false } : s);
		this.cdr.markForCheck();
		try { await this.chat.renameSession(startUtc, next); }
		catch (err) { console.error('Failed to rename chat session', err); }
	}

	async archiveSession(event: Event, session: PmChatSessionSummary): Promise<void> {
		event.stopPropagation();
		try {
			await this.chat.archiveSession(session.startUtc);
			this.sessions = this.sessions.filter(s => s.startUtc !== session.startUtc);
			if (this.convo.sessionStartUtc === session.startUtc) { this.newChat(); }
			this.cdr.markForCheck();
		} catch { /* leave the row on failure */ }
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
		this.routeSub?.unsubscribe();
		this.convo.destroy();
	}

	private refreshSessions(): void {
		this.chat.listSessions()
			.then(list => { this.sessions = list; this.cdr.markForCheck(); })
			.catch(() => { /* backend unreachable / not signed in — leave the sidebar empty */ });
	}

	private upsertSession(session: PmChatSessionSummary): void {
		this.sessions = [session, ...this.sessions.filter(s => s.startUtc !== session.startUtc)];
		this.cdr.markForCheck();
	}
}
